import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import RegenerationModal from '../../../components/ui/RegenerationModal';
import { plannerService } from '../../../services/plannerService';
import { plannerStorageService } from '../../../services/plannerStorageService';
import { plannerRegenerationService } from '../../../services/plannerRegenerationService';

const PlannerCard = ({ planner, onDelete, onDownload, onRefresh }) => {
  const [showActions, setShowActions] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerationLogs, setRegenerationLogs] = useState([]);
  const [regenerationProgress, setRegenerationProgress] = useState(0);
  const [regenerationTotalPages, setRegenerationTotalPages] = useState(0);
  const navigate = useNavigate();

  // Load preview image URL if thumbnail data exists
  useEffect(() => {
    const loadPreviewUrl = async () => {
      if (planner?.thumbnailData) {
        setLoadingPreview(true);
        try {
          const url = await plannerService?.getPreviewImageUrl(planner?.thumbnailData);
          setPreviewUrl(url);
        } catch (error) {
          console.error('Error loading preview URL:', error);
        } finally {
          setLoadingPreview(false);
        }
      }
    };

    loadPreviewUrl();
  }, [planner?.thumbnailData]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString)?.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getColorSchemePreview = (colorScheme) => {
    const schemes = {
      monochrome: ['#000000', '#333333', '#666666', '#999999'],
      blue: ['#1E3A8A', '#3B82F6', '#60A5FA', '#93C5FD'],
      green: ['#065F46', '#10B981', '#34D399', '#6EE7B7'],
      purple: ['#5B21B6', '#8B5CF6', '#A78BFA', '#C4B5FD'],
      pink: ['#BE185D', '#EC4899', '#F472B6', '#F9A8D4'],
      orange: ['#C2410C', '#F97316', '#FB923C', '#FDBA74'],
      default: ['#475569', '#64748B', '#94A3B8', '#CBD5E1']
    };

    return schemes?.[colorScheme] || schemes?.default;
  };

  const handleDownload = async () => {
    // Check if PDF URL exists
    if (!planner?.generatedPdfPath) {
      // Trigger regeneration
      handleRegenerate();
      return;
    }

    setDownloading(true);
    try {
      // Get signed URL for the PDF
      const signedUrl = await plannerStorageService?.getSignedUrl(planner?.generatedPdfPath);
      
      if (!signedUrl) {
        throw new Error('Failed to get download URL');
      }

      // Record the download
      await plannerService?.recordDownload(planner?.id);

      // Trigger browser download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = `${planner?.name}.pdf`;
      link.target = '_blank';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);

      // Refresh the list to update download count
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error downloading planner:', error);
      alert(`Failed to download planner: ${error?.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleRegenerate = () => {
    setRegenerating(true);
    setRegenerationLogs([]);
    setRegenerationProgress(0);
    setRegenerationTotalPages(0);

    // Start regeneration process
    plannerRegenerationService?.regeneratePlanner(planner?.id, ({ message, progress, totalPages }) => {
        // Update logs
        const timestamp = new Date()?.toLocaleTimeString();
        setRegenerationLogs(prev => [...prev, { timestamp, message }]);
        
        // Update progress
        setRegenerationProgress(progress || 0);
        
        // Update total pages estimate
        if (totalPages) {
          setRegenerationTotalPages(totalPages);
        }
      })?.then((result) => {
        // Success - navigate to results page
        setTimeout(() => {
          setRegenerating(false);
          
          // Navigate to pdf-generation-results with regenerated planner data
          navigate('/pdf-generation-results', {
            state: {
              plannerData: result?.plannerData,
              fromRegeneration: true,
              configId: planner?.id
            }
          });
        }, 1000);
      })?.catch((error) => {
        console.error('Regeneration failed:', error);
        setRegenerationLogs(prev => [
          ...prev, 
          { 
            timestamp: new Date()?.toLocaleTimeString(), 
            message: `FAILED: ${error?.message}` 
          }
        ]);
        
        // Close modal after showing error for 3 seconds
        setTimeout(() => {
          setRegenerating(false);
          alert(`Regeneration failed: ${error?.message}`);
        }, 3000);
      });
  };

  const getConfigSummary = () => {
    const config = planner?.configurationData || {};
    const parts = [];

    // Format planner type from configuration data
    const formatPlannerType = (configData, layoutTypeColumn) => {
      if (!configData || typeof configData !== 'object') {
        return null;
      }

      const features = [];
      
      // Check multiple possible locations for layout configuration
      // Priority: 1) nested under 'layout', 2) direct properties, 3) layoutType column
      const layoutConfig = configData?.layout || configData;
      
      // Try to parse layoutType column if it's a JSON string
      let parsedLayoutType = null;
      if (layoutTypeColumn && typeof layoutTypeColumn === 'string') {
        try {
          parsedLayoutType = JSON.parse(layoutTypeColumn);
        } catch (e) {
          // Not JSON, ignore
        }
      }
      
      // Use parsedLayoutType as fallback if layout config doesn't have the properties
      const finalConfig = parsedLayoutType || layoutConfig;
      
      if (finalConfig?.yearlyOverview) features?.push('Yearly');
      if (finalConfig?.monthPlanner) features?.push('Monthly');
      if (finalConfig?.weekPlanner) features?.push('Weekly');
      if (finalConfig?.dailyPlanner?.enabled) features?.push('Daily');
      if (finalConfig?.meetingNotes) features?.push('Meetings');
      if (finalConfig?.notes) features?.push('Notes');
      
      if (features?.length === 0) {
        return null;
      }
      
      if (features?.length > 2) {
        return `${features?.[0]} + ${features?.length - 1} more`;
      }
      
      return features?.join(' + ');
    };

    // Try to format the configuration data as planner type
    const plannerType = formatPlannerType(config, planner?.layoutType);
    if (plannerType) {
      parts?.push(plannerType);
    }

    if (planner?.layoutType && !plannerType) {
      // Only show layoutType if we couldn't parse features from config
      parts?.push(planner?.layoutType?.charAt(0)?.toUpperCase() + planner?.layoutType?.slice(1));
    }

    if (planner?.pageSize) {
      parts?.push(planner?.pageSize);
    }

    // Only check for sections array if we haven't found planner type
    if (!plannerType && config?.sections && Array.isArray(config?.sections)) {
      parts?.push(`${config?.sections?.length} sections`);
    }

    return parts?.join(' • ') || 'Standard configuration';
  };

  // Add this block - declare hasGeneratedPdf variable
  const hasGeneratedPdf = !!planner?.generatedPdfPath;

  return (
    <>
      <div 
        className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 group"
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {/* Thumbnail / Color Scheme Preview */}
        <div className="relative h-48 bg-muted overflow-hidden">
          {loadingPreview ? (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="Loader2" size={32} className="animate-spin text-muted-foreground" />
            </div>
          ) : previewUrl ? (
            <Image
              src={previewUrl}
              alt={`Preview of ${planner?.name}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            // Color scheme preview as fallback
            (<div className="w-full h-full grid grid-cols-2 grid-rows-2">
              {getColorSchemePreview(planner?.colorScheme)?.map((color, index) => (
                <div
                  key={index}
                  className="transition-transform group-hover:scale-110"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>)
          )}

          {/* Quick Actions Overlay */}
          <div className={`
            absolute inset-0 bg-black/50 flex items-center justify-center space-x-2
            transition-opacity duration-200
            ${showActions ? 'opacity-100' : 'opacity-0'}
          `}>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownload}
              iconName="Download"
              iconPosition="left"
              iconSize={14}
            >
              Download
            </Button>
          </div>
        </div>
        {/* Content */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-heading font-semibold text-foreground line-clamp-1 flex-1">
              {planner?.name}
            </h3>
          </div>

          {/* Configuration Summary */}
          <p className="font-body text-sm text-muted-foreground mb-3">
            {getConfigSummary()}
          </p>

          {/* Planner Details */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-xs">
              <span className="font-caption text-muted-foreground">Color Scheme:</span>
              <div className="flex items-center space-x-1">
                <span className="font-caption text-foreground capitalize">{planner?.colorScheme}</span>
                <div className="flex space-x-1">
                  {getColorSchemePreview(planner?.colorScheme)?.slice(0, 3)?.map((color, index) => (
                    <div
                      key={index}
                      className="w-3 h-3 rounded-full border border-border"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="font-caption text-muted-foreground">Created:</span>
              <span className="font-caption text-foreground">{formatDate(planner?.createdAt)}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              iconName={hasGeneratedPdf ? 'Download' : 'RefreshCw'}
              iconPosition="left"
              iconSize={14}
              className="flex-1"
              disabled={downloading || regenerating}
            >
              {downloading ? 'Downloading...' : regenerating ?'Regenerating...': (hasGeneratedPdf ?'Download' : 'Regenerate')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(planner)}
              iconName="Trash2"
              iconSize={14}
              disabled={regenerating}
            />
          </div>
        </div>
      </div>

      {/* Regeneration Modal */}
      <RegenerationModal
        isOpen={regenerating}
        onClose={() => setRegenerating(false)}
        logs={regenerationLogs}
        progress={regenerationProgress}
        totalPages={regenerationTotalPages}
      />
    </>
  );
};

export default PlannerCard;